import React, { useCallback } from 'react';
import { Divider, IconButton, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AddPhotoAlternateOutlined as SetImageIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  List as DrillDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listViewItemStyles } from '../../../styles';
import { ITopic } from '../../../../dataObjects/models/topics/Topic';
import { IUserAccessPermissionsForObject } from '../../../../dataObjects/models/collaboration/ObjectUserPermissions';
import { TooltipStringAssets } from '../../../../assets/stringAssets';
import { setCurrentTopic } from "../../../../uiMiddleware-redux/slices/topic/currentTopicSlice";
import { useAppDispatch } from '../../../../uiMiddleware-redux/store/configureStore';
import { ITopicViewModel } from '../../../../dataObjects/viewModels/topicViewModel';
import { enumDigitalMediaDisplayEnvironment, enumImageFileSize, enumPersistableObjectType } from '../../../../dataObjects/enums';
import { ImageMediaDisplayForProfileImage } from '../../../controls/digitalMediaDisplays/ImageMediaDisplayForProfileImage';


// The next line is preserved for update to React 18, once peer dependencies will allow the update
// export interface TopicsListViewItemProps extends PropsWithChildren<unknown> {
export interface TopicsListViewItemProps {
  topicViewModel: ITopicViewModel;
  userPermissions: IUserAccessPermissionsForObject;
  onDelete: (topic: ITopic) => void;
  onDeleteProfileImage: (topicViewModel: ITopicViewModel) => void;
}

export const TopicsListViewItem: React.FC<TopicsListViewItemProps> = (props: TopicsListViewItemProps) => {
  TopicsListViewItem.displayName = 'Topics List View Item';

  // eslint-disable-next-line no-console
  // console.info(`Render TopicsListViewItem`);

  const classes: Record<string, string> = listViewItemStyles();

  const { onDelete, onDeleteProfileImage, topicViewModel, userPermissions } = props;
  const { topic, profileImage } = topicViewModel;
  const { id, name, description } = topic;

  const dispatch = useAppDispatch();

  // the 'useNavigate' hook will be used to navigate the browser
  const navigate = useNavigate();

  // memoized function that handles click for drilldown to topicItems
  const handleTopicItemsDrilldownClicked: () => void = useCallback((): void => {
    // first, dispatch a request to set the current Topic in Redux state to be the one passed to this component
    dispatch(setCurrentTopic(topic));

    // then, use the navigate object to navigate to the TopicItems page
    navigate(`/${id}/topicItems`)
  },
    [dispatch, navigate, id, topic],
  );

  return (
    <>
      <div className={classes.tableRow} key={id}>
        {/* Data Row 1: Name & Action Buttons  */}
        <Grid container size={12} direction="row" >
          {/* Name */}
          <Grid container size={{ xs: 8, sm: 9 }} >
            <Typography className={classes.title} variant="h6">
              {name}
            </Typography>
          </Grid>
          {/* Action Buttons */}
          <Grid container size={{ xs: 4, sm: 3 }} direction="row" justifyContent="center" alignContent="flex-start">
            {/* Topics button */}
            <Grid container className={classes.actionButtonContainer} size={4} justifyContent='center' >
              {/* the tooltip for the drilldown button will either be "Drill down" (if user has sufficient permission) or "View XXX" (insufficient permission) */}
              <Tooltip title={!userPermissions.hasViewPermission ? TooltipStringAssets.drillDown_Disallowed : `View ${topic.topicItemNameAliasPlural}`} arrow>
                <IconButton
                  size="small"
                  className={`${classes.actionButton} ${classes.drillDownIconButton}`}
                  disabled={!userPermissions.hasViewPermission}
                  onClick={() => handleTopicItemsDrilldownClicked()}
                >
                  <DrillDownIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            {/* Edit button */}
            <Grid container className={classes.actionButtonContainer} size={4} justifyContent='center' >
              <Tooltip
                // the tooltip for the edit button will either be "Edit" (if user has sufficient permission) or "Edit disallowed" (insufficient permission)
                title={!userPermissions.hasEditPermission ? TooltipStringAssets.edit_Disallowed : TooltipStringAssets.edit}
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    className={`${classes.actionButton} ${classes.editIconButton}`}
                    disabled={!userPermissions.hasEditPermission}
                    onClick={() => id && navigate(`/topic/${id}`)}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
            {/* Delete button */}
            <Grid container className={classes.actionButtonContainer} size={4} justifyContent='center' >
              <Tooltip
                // the tooltip for the delete button will either be "Delete" (if user has sufficient permission) or "Delete disallowed" (insufficient permission)
                title={!userPermissions.hasEditPermission ? "Delete disallowed" : "Delete"}
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    className={`${classes.actionButton} ${classes.deleteIconButton}`}
                    disabled={!userPermissions.hasEditPermission}
                    onClick={() => topic && onDelete(topic)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        {/* Data Row 2: Profile Image & Description (stacked)  */}
        <Grid container size={12} >
          <Grid size={12}>
            {(profileImage === undefined) &&
              <div className={classes.setProfileImageButtonContainer}>
                <Tooltip
                  // the tooltip for the edit button will either be "Set profile image" (if user has sufficient permission) or "Set profile image disallowed" (insufficient permission)
                  title={!userPermissions.hasEditPermission ? TooltipStringAssets.setProfileImage_Disallowed : TooltipStringAssets.setProfileImage}
                  arrow
                >
                  <span>
                    <IconButton
                      style={{ borderRadius: '50%' }} // to make it a round button
                      className={`${classes.actionIconButton} ${classes.editIconButton}`}
                      size="medium"
                      aria-label="edit"
                      // this button will be disabled if the user doesn't have Edit permissions
                      disabled={!userPermissions.hasEditPermission}
                      onClick={() => navigate(`/${id}/${enumPersistableObjectType.Channel}/profileImageLink`)} >
                      <SetImageIcon fontSize='large' />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            }
            {(profileImage !== undefined) &&
              <Grid container justifySelf={'flex-start'} justifyContent={'flex-start'}>
                <Grid container direction={'column'} >
                  <Grid container justifyContent={'center'} >
                    <ImageMediaDisplayForProfileImage
                      imageLink={profileImage}
                      displayEnvironment={enumDigitalMediaDisplayEnvironment.CardView}
                      displayImageSize={enumImageFileSize.Small}
                      displayAsRound={true}
                    />
                  </Grid>
                  <Grid container justifyContent={'center'} >
                    <Tooltip
                      // the tooltip for the Delete Profile Image button will either be "Delete profile image" (if user has sufficient permission) or "Delete profile image disallowed" (insufficient permission)
                      title={!userPermissions.hasEditPermission ? TooltipStringAssets.deleteProfileImage_Disallowed : TooltipStringAssets.deleteProfileImage}
                      arrow
                    >
                      <span>
                        <IconButton
                          style={{ borderRadius: '50%', marginTop: '-30px', marginBottom: '5px', scale: '0.8' }}
                          className={`${classes.actionIconButton} ${classes.deleteIconButton}`}
                          size="small"
                          aria-label="delete profile image"
                          // this button will be disabled if the user doesn't have Edit permissions -OR- the Channel is a private Channel
                          disabled={!userPermissions.hasEditPermission}
                          onClick={() => onDeleteProfileImage(topicViewModel)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            }

          </Grid>
          <Grid container size={12} >
            <Typography className={classes.description} variant="body1">
              {description}
            </Typography>
          </Grid>
        </Grid>

        {/* Add a dividing line after each item */}
        <Divider />
      </div>
    </>
  );

}

export default TopicsListViewItem;